/**
 * Canonical Cursor cloud advise prompt template (system + user structure).
 * Used by cursorAgentAdvise at runtime and by the Agentic Instructions UI.
 * Keep placeholders and payload shape in sync with buildAdviseUserPrompt.
 */

/** Obvious placeholders for live context injected at advise time. */
export const PROMPT_PLACEHOLDERS = {
  STRATEGY: "<STRATEGY>",
  STRATEGY_LABEL: "<STRATEGY_LABEL>",
  STRATEGY_ROLE: "<STRATEGY_ROLE>",
  STRATEGY_IMPLIES: "<STRATEGY_IMPLIES>",
  STRATEGY_IMPLIES_JSON: "<STRATEGY_IMPLIES_JSON>",
  AGENT_KIND: "<AGENT_KIND>",
  PORTFOLIO_KIND: "<PORTFOLIO_KIND>",
  BOOK_SOURCE: "<BOOK_SOURCE>",
  SLOT: "<SLOT>",
  WANT: "<WANT>",
  AGENT_SETTINGS: "<AGENT_SETTINGS>",
  USER_INSTRUCTIONS: "<USER_INSTRUCTIONS>",
  /** Income-targeting basis: own capital for paper books, account value for advisors. */
  ACCOUNT_VALUE: "<ACCOUNT_VALUE>",
  /** Monthly $ income goal ≈ expectedMonthlyRocPct × account value. */
  TARGET_INCOME: "<TARGET_INCOME>",
  CASH_AND_BUYING_POWER: "<CASH_AND_BUYING_POWER>",
  BOOK_CASH: "<BOOK_CASH>",
  BOOK_BUYING_POWER: "<BOOK_BUYING_POWER>",
  BOOK_EQUITY: "<BOOK_EQUITY>",
  BOOK_RESERVED: "<BOOK_RESERVED>",
  OPEN_POSITIONS: "<OPEN_POSITIONS>",
  EQUITY_POSITIONS: "<EQUITY_POSITIONS>",
  OWED_SHARES: "<OWED_SHARES>",
  /** JSON string of OWED_SHARES_SEMANTICS prose (distinct from the owedShares array). */
  OWED_SHARES_SEMANTICS_JSON: "<OWED_SHARES_SEMANTICS>",
  WHEEL_ELIGIBILITY: "<WHEEL_ELIGIBILITY>",
  RECENT_FILLS: "<RECENT_FILLS>",
  PORTFOLIO_TRADES: "<PORTFOLIO_TRADES>",
  RECENT_MEMORY: "<RECENT_MEMORY>",
  /** Prior Cursor feedback (questions / prompt ideas) from recent advise memory. */
  FEEDBACK: "<FEEDBACK>",
  /** Human accept/dismiss verdicts on prior recommendations, with optional comments. */
  USER_DECISIONS: "<USER_DECISIONS>",
  CANDIDATES: "<CANDIDATES>",
  SYSTEM_PROMPT: "<SYSTEM_PROMPT>",
  ALLOW_WEB: "<ALLOW_WEB>",
  WEB_RESEARCH_RULES: "<WEB_RESEARCH_RULES>",
  FINAL_REPLY_RULE: "<FINAL_REPLY_RULE>",
};

/** Stable id for the seeded Wheel default template (user-level store). */
export const BUILTIN_PROMPT_TEMPLATE_ID = "wheel-default";
export const BUILTIN_PROMPT_TEMPLATE_NAME = "Wheel (default)";

const MAX_TEMPLATE_BODY_CHARS = 120_000;
const MAX_TEMPLATE_NAME_CHARS = 80;

/** Echoed on book.owedSharesSemantics / wheelEligibility for the Cursor trader. */
export const OWED_SHARES_SEMANTICS =
  "owedShares = long stock on this agent's private paper ledger (from paper fills/assignments) that the Wheel aims to call away — NOT live SF+/broker /wheel/stock-positions. freeSharesForCalls = sharesHeld − sharesReservedForOpenCalls (open short calls on the paper options book). contractsAvailableForNewCCs = floor(free/100). Sell CCs only when canSellCoveredCall is true. Source is paper_ledger.";

export const LIVE_OWED_SHARES_SEMANTICS =
  "owedShares = long stock on the trader's live own-ledger (assignment strike avg cost) that the Wheel aims to call away — NOT this agent's paper book. freeSharesForCalls = sharesHeld − sharesReservedForOpenCalls (open short calls on the live options book). contractsAvailableForNewCCs = floor(free/100). Sell CCs only when canSellCoveredCall is true. Source is live_portfolio.";

/**
 * @param {string} strategy
 * @param {{ kind?: string }} [opts]
 */
export function strategyPlaybook(strategy, opts = {}) {
  const s = String(strategy || "").toLowerCase() === "wheel" ? "wheel" : "discretion";
  const kind =
    String(opts.kind || "").toLowerCase() === "advisor" ? "advisor" : "paper";
  if (kind === "advisor") {
    if (s === "wheel") {
      return {
        strategy: "wheel",
        kind: "advisor",
        role: "live-portfolio Wheel advisor",
        implies: [
          "You ADVISE the human trader on their LIVE brokerage portfolio (own ledger + Robinhood cash/buying power). You do not place paper fills or live broker orders.",
          "book.openOptionPositions / equityPositions / owedShares / cash / buyingPower are the LIVE book (bookSource=live_portfolio) — not a paper sim.",
          "No owed/long live shares (or no free shares for CC) → ONLY cash-secured puts are eligible. Do not recommend covered calls unless book.owedShares shows canSellCoveredCall (freeSharesForCalls ≥ 100).",
          "Use book.owedShares for CC advice: sharesHeld, sharesReservedForOpenCalls, freeSharesForCalls / contractsAvailableForNewCCs, avgCost/costBasis (assignment strike), currentPrice, daysHeld, and relatedOpenShortCalls. Prefer strikes at/above avgCost unless Risky calls are enabled.",
          "Names already in owedShares / long live stock → prefer covered calls when free shares allow; do not recommend additional CSPs on those names.",
          "HARD: never recommend open puts whose secured capital (strike × 100 × qty) exceeds available cash/buyingPower; prefer skip or pass. Covered calls only when live owed/free shares allow.",
          "HARD: never recommend an open with premium ≤ 0, missing bid/ask, or a contract that is not in the provided candidate list. Skip worthless / no-quote names (do not invent strikes).",
          "Put opens count against want; covered-call opens are share-backed and do not consume put room (propose CCs whenever owedShares.canSellCoveredCall).",
          "When buyingPower cannot collateralize any CSP, stop asking about diversification — focus on covered calls and capital build from premium.",
          "stopLossMode=keep (and Wheel puts under either when thesis intact / near basis): prefer hold-to-assignment over mechanical BTC. stopLossMode=stop: BTC at the stop. Assignment is not a stop-loss failure.",
          "Prefer packed book/screener mids over ad-hoc web quotes; discard non-USD or clearly stale web prices.",
          "Do not re-ask unanswered questions already listed in <FEEDBACK>; at most one brief re-flag if still blocking.",
          "Actions are order intents (same JSON as paper: open/close/hold/skip with optionSymbol, qty, side). Execution is advise-only for now — Linode records them and marks screener rows. Do not assume a live broker or paper fill.",
        ],
      };
    }
    return {
      strategy: "discretion",
      kind: "advisor",
      role: "live-portfolio options advisor",
      implies: [
        "You ADVISE the human trader on their LIVE brokerage portfolio (own ledger + Robinhood cash/buying power). You do not place paper fills or live broker orders.",
        "Select short puts/calls from the provided SF+ candidate universe per agent Sources and risk settings, against the LIVE book (bookSource=live_portfolio).",
        "HARD: never recommend open puts whose secured capital (strike × 100 × qty) exceeds available cash/buyingPower; prefer skip or pass.",
        "HARD: never recommend an open with premium ≤ 0, missing bid/ask, or a contract that is not in the provided candidate list. Skip worthless / no-quote names (do not invent strikes).",
        "Prefer packed book/screener mids over ad-hoc web quotes; discard non-USD or clearly stale web prices.",
        "Do not re-ask unanswered questions already listed in <FEEDBACK>; at most one brief re-flag if still blocking.",
        "Actions are order intents (same JSON as paper: open/close/hold/skip with optionSymbol, qty, side). Execution is advise-only for now — Linode records them and marks screener rows. Do not assume a live broker or paper fill.",
      ],
    };
  }
  if (s === "wheel") {
    return {
      strategy: "wheel",
      kind: "paper",
      role: "Wheel strategy trader",
      implies: [
        "You trade the Wheel on this agent's private paper portfolio only: sell cash-secured puts (CSP) to acquire shares, then sell covered calls (CC) once paper shares are owed/held.",
        "book.openOptionPositions / equityPositions / owedShares are the paper ledger — never treat live SF+ or brokerage holdings as this agent's book.",
        "No owed/long paper shares (or no free shares for CC) → ONLY cash-secured puts are eligible. Do not pick covered calls unless book.owedShares shows canSellCoveredCall (freeSharesForCalls ≥ 100).",
        "Use book.owedShares for CC decisions: sharesHeld, sharesReservedForOpenCalls, freeSharesForCalls / contractsAvailableForNewCCs, avgCost/costBasis, currentPrice, daysHeld, and relatedOpenShortCalls. Prefer strikes at/above avgCost unless Risky calls are enabled.",
        "Names already in owedShares / long paper stock → prefer covered calls when free shares allow; do not sell additional CSPs on those names.",
        "HARD: never propose open puts whose secured capital (strike × 100 × qty) exceeds available cash/buyingPower; prefer skip or pass. Covered calls only when paper owed/free shares allow.",
        "Put opens count against want; covered-call opens are share-backed and do not consume put room (propose CCs whenever owedShares.canSellCoveredCall even when want=0).",
        "When buyingPower cannot collateralize any CSP, stop asking about diversification — focus on covered calls and capital build from premium.",
        "stopLossMode=keep (and Wheel puts under either when thesis intact / near basis): prefer hold-to-assignment over mechanical BTC. stopLossMode=stop: BTC at the stop. Assignment is not a stop-loss failure.",
        "Prefer packed book/screener mids over ad-hoc web quotes; discard non-USD or clearly stale web prices.",
        "Do not re-ask unanswered questions already listed in <FEEDBACK>; at most one brief re-flag if still blocking.",
        "Hard risk filters, cash/collateral, sizing, and Wheel eligibility are enforced in code — your actions are proposals that rules may reject or resize.",
      ],
    };
  }
  return {
    strategy: "discretion",
    kind: "paper",
    role: "discretionary options trader",
    implies: [
      "You select short puts/calls from the provided SF+ candidate universe per agent Sources and risk settings, against this agent's private paper book only.",
      "HARD: never propose open puts whose secured capital (strike × 100 × qty) exceeds available cash/buyingPower; prefer skip or pass.",
      "Prefer packed book/screener mids over ad-hoc web quotes; discard non-USD or clearly stale web prices.",
      "Do not re-ask unanswered questions already listed in <FEEDBACK>; at most one brief re-flag if still blocking.",
      "Hard risk filters, cash/collateral, and sizing are enforced in code — your actions are proposals that rules may reject or resize.",
    ],
  };
}

/**
 * Shared JSON schema block (identical for allowWeb on/off).
 */
/** Pre-feedback schema body — used to migrate stored bootstrap templates. */
export const OUTPUT_SCHEMA_CORE_LEGACY = `Decide the next actions for this timeslot from the provided book, settings, memory, and SF+ candidate universe.
Prefer several concrete actions when the book has room and cash allows (up to "want" opens). Diversify underlyings.

Reply with ONLY valid JSON (no markdown fences, no prose outside JSON) matching:
{
  "summary": "short portfolio-level rationale",
  "pass": false,
  "actions": [
    {
      "type": "open" | "close" | "skip" | "hold",
      "symbol": "UNDERLYING",
      "optionSymbol": "OCC or empty",
      "qty": 1,
      "side": "put" | "call" | "",
      "rationale": "why"
    }
  ]
}
Rules:
- "open" actions must reference candidates from the provided list (by optionSymbol and/or symbol/underlying).
- HARD: never open a contract with premium ≤ 0, a missing/zero bid, or one that is not in the candidate list. Skip worthless / no-quote contracts; do not invent strikes.
- At most "want" open actions; prefer diversified underlyings.
- Prefer earnings-clean names when candidates include nextEarningsDate / daysToEarnings / earningsBeforeExp (skip or size down when earningsBeforeExp is true unless instructions say otherwise).
- qty is a suggestion (integer ≥ 1); code may resize. Use side put/call when known.
- "close" / "hold" / "skip" may reference open positions. Linode executes Cursor close decisions this slot; only expiry/assignment lifecycle is automatic outside Cursor.
- Set pass=true and actions=[] when you recommend no new opens and no other actions.
- Legacy "picks" / "analysis" fields are accepted but prefer actions + summary.
- Be concise.`;

/** Schema text before portfolio-summary note — used to soft-migrate stored templates. */
export const OUTPUT_SCHEMA_CORE_PRE_SUMMARY = `Decide the next actions for this timeslot from the provided book, settings, memory, and SF+ candidate universe.
Prefer several concrete actions when the book has room and cash allows (up to "want" opens). Diversify underlyings.

Reply with ONLY valid JSON (no markdown fences, no prose outside JSON) matching:
{
  "summary": "short portfolio-level rationale",
  "pass": false,
  "actions": [
    {
      "type": "open" | "close" | "skip" | "hold",
      "symbol": "UNDERLYING",
      "optionSymbol": "OCC or empty",
      "qty": 1,
      "side": "put" | "call" | "",
      "rationale": "why"
    }
  ],
  "feedback": {
    "questions": ["optional clarifying questions for better future decisions"],
    "promptIdeas": ["optional ideas to improve the prompt / instructions"],
    "notes": "optional free text"
  },
  "webUsed": false,
  "webQueries": [
    { "query": "search terms or URL you looked up", "purpose": "optional one-line why it mattered" }
  ]
}
Rules:
- "open" actions must reference candidates from the provided list (by optionSymbol and/or symbol/underlying).
- HARD: never open a contract with premium ≤ 0, a missing/zero bid, or one that is not in the candidate list. Skip worthless / no-quote contracts; do not invent strikes.
- At most "want" open actions; prefer diversified underlyings.
- Prefer earnings-clean names when candidates include nextEarningsDate / daysToEarnings / earningsBeforeExp (skip or size down when earningsBeforeExp is true unless instructions say otherwise).
- qty is a suggestion (integer ≥ 1); code may resize. Use side put/call when known.
- "close" / "hold" / "skip" may reference open positions. Linode executes Cursor close decisions this slot; only expiry/assignment lifecycle is automatic outside Cursor.
- Set pass=true and actions=[] when you recommend no new opens and no other actions.
- Optional "feedback": questions for the human trader, promptIdeas to improve future instructions, and/or notes. Omit feedback (or use empty lists) when none. Prior feedback is injected via <FEEDBACK> on later slots.
- webUsed / webQueries: when settings.allowWeb is true, always set webUsed (true if you searched, browsed, or fetched any URL; false if you did not). When webUsed is true, list each distinct search query, URL, or lookup in webQueries (query required; purpose optional). When allowWeb is false, set webUsed=false and webQueries=[] (or omit).
- Legacy "picks" / "analysis" fields are accepted but prefer actions + summary.
- Be concise.`;

export const OUTPUT_SCHEMA_CORE = `Decide the next actions for this timeslot from the provided book, settings, memory, and SF+ candidate universe.
Prefer several concrete actions when the book has room and cash allows (up to "want" opens). Diversify underlyings.
"No trade" (pass=true, actions=[]) is valid when risk/reward or concentration argues against opening.

Reply with ONLY valid JSON (no markdown fences, no prose outside JSON) matching:
{
  "summary": "short portfolio-level rationale (cash, share/option exposure, concentration, catalysts as needed)",
  "pass": false,
  "actions": [
    {
      "type": "open" | "close" | "skip" | "hold",
      "symbol": "UNDERLYING",
      "optionSymbol": "OCC or empty",
      "qty": 1,
      "side": "put" | "call" | "",
      "rationale": "why — include key economics (premium, capital, break-even / ROC when opening; management plan when closing/holding)"
    }
  ],
  "feedback": {
    "questions": ["optional clarifying questions for better future decisions"],
    "promptIdeas": ["optional ideas to improve the prompt / instructions"],
    "notes": "optional free text"
  },
  "webUsed": false,
  "webQueries": [
    { "query": "search terms or URL you looked up", "purpose": "optional one-line why it mattered" }
  ]
}
Rules:
- Slot replies are this JSON only — not prose approval letters. Propose actions; Linode paper rules may reject or resize. No live broker orders.
- "open" actions must reference candidates from the provided list (by optionSymbol and/or symbol/underlying).
- HARD: never open a contract with premium ≤ 0, a missing/zero bid, or one that is not in the candidate list. Skip worthless / no-quote contracts; do not invent strikes.
- At most "want" cash-secured put opens; covered-call opens are share-backed and do NOT count against want (propose them whenever owedShares.canSellCoveredCall). Prefer diversified underlyings.
- Prefer earnings-clean names when candidates include nextEarningsDate / daysToEarnings / earningsBeforeExp (skip or size down when earningsBeforeExp is true unless instructions say otherwise).
- qty is a suggestion (integer ≥ 1); code may resize. Use side put/call when known.
- "close" / "hold" / "skip" may reference open positions. Linode executes Cursor close decisions this slot; only expiry/assignment lifecycle is automatic outside Cursor. Recycle/auto-recycle turns never apply closes.
- Set pass=true and actions=[] when you recommend no new opens and no other actions.
- "summary" may briefly cover portfolio state that informs the decision (cash, exposure, concentration, upcoming catalysts).
- Optional "feedback": questions for the human trader, promptIdeas to improve future instructions, and/or notes. Omit feedback (or use empty lists) when none. Prior feedback is injected via <FEEDBACK> on later slots — do not repeat the same unanswered question; at most one brief re-flag.
- When candidateFilter shows unaffordable puts omitted / candidates empty and cash cannot fund a CSP, do not keep asking about diversification — focus on CCs / capital build.
- webUsed / webQueries: when settings.allowWeb is true, always set webUsed (true if you searched, browsed, or fetched any URL; false if you did not). When webUsed is true, list each distinct search query, URL, or lookup in webQueries (query required; purpose optional). When allowWeb is false, set webUsed=false and webQueries=[] (or omit).
- Legacy "picks" / "analysis" fields are accepted but prefer actions + summary + optional feedback + webUsed/webQueries when allowWeb.
- Be concise.`;

/** allowWeb: false — tight JSON-only, no research tools. */
export const WEB_RESEARCH_RULES_OFF = `- settings.allowWeb is false: decide from packed SF+ book, settings, memory, and candidates only.
- Do not browse, search the web, fetch URLs, or run shell for extra information.
- Do not create PRs, edit files, or run shell tools. Final reply is JSON only — no tool use.`;

/** allowWeb: true — may use cloud network / computer-use browse before deciding. */
export const WEB_RESEARCH_RULES_ON = `- settings.allowWeb is true: you MAY use the cloud VM network, browser/computer-use, or available search tools for earnings, news, or catalysts that matter to candidates or open positions.
- Prefer packed SF+ candidates, book mids, and screener quotes first. Search/browse only when the missing fact could change open/close/pass or sizing; skip research when the book already decides.
- Discard web prices that are non-USD, historical-period contaminated, or clearly stale versus packed book mids — book/screener mids win on conflicts.
- Still no PRs, file edits, git pushes, or trading via shell — research only, then decide.
- Intermediate tool use is allowed. Your FINAL assistant message must be the same JSON schema only (no markdown fences, no prose outside JSON).
- In that JSON, report webUsed=true and every query/URL you searched in webQueries when you used the web; webUsed=false and webQueries=[] when you did not.`;

/**
 * @param {boolean} [allowWeb]
 */
export function buildWebResearchRules(allowWeb) {
  return allowWeb === true ? WEB_RESEARCH_RULES_ON : WEB_RESEARCH_RULES_OFF;
}

/**
 * @param {boolean} [allowWeb]
 */
export function buildFinalReplyRule(allowWeb) {
  return allowWeb === true
    ? "You may research first if needed, then return JSON only (actions + summary + optional feedback + webUsed/webQueries) as the final message."
    : "Return JSON only (actions + summary; optional feedback; webUsed=false).";
}

/**
 * Full rules block for system prompt.
 * @param {boolean} [allowWeb]
 */
export function buildOutputSchemaRules(allowWeb, opts = {}) {
  const advisorNote =
    String(opts.kind || "").toLowerCase() === "advisor"
      ? `
- Advisor / live-portfolio mode: the same action JSON is an order intent (open/close/hold/skip with optionSymbol, qty, side). Execution is advise-only for now — Linode records intents and marks matching Puts/Calls/Options rows. It does not paper-fill or send broker orders. A later live-execution switch can consume these same intents.
- payload.userDecisions holds the trader's verdict on your prior recommendations (decision accepted | dismissed, with an optional free-text comment). Treat the comments as standing guidance: address the reasons behind dismissals instead of re-proposing the same idea unchanged, and lean into what was accepted. Reference a decision in your summary when it changed your thinking.`
      : "";
  return `${OUTPUT_SCHEMA_CORE}${advisorNote}
${buildWebResearchRules(allowWeb)}`;
}

/** @deprecated Prefer buildOutputSchemaRules(allowWeb). Default = allowWeb off. */
export const OUTPUT_SCHEMA_RULES = buildOutputSchemaRules(false);

/**
 * Build system prompt for the active strategy (runtime: filled role/strategy).
 * @param {string} [strategy]
 * @param {{ allowWeb?: boolean }} [opts]
 */
export function buildSystemPrompt(strategy, opts = {}) {
  const playbook = strategyPlaybook(strategy, { kind: opts.kind });
  const label = playbook.strategy === "wheel" ? "Wheel" : "Discretion";
  const allowWeb = opts.allowWeb === true;
  const portfolioKind =
    playbook.kind === "advisor"
      ? "live brokerage portfolio (recommendations only)"
      : "paper portfolio";
  return `You are the ${playbook.role} for this Stock Forge Agentic ${portfolioKind}.
Strategy in use: ${label}.
${playbook.implies.map((line) => `- ${line}`).join("\n")}

${buildOutputSchemaRules(allowWeb, { kind: playbook.kind })}`;
}

/**
 * System prompt with placeholders (for Instructions UI template view).
 * @param {{ allowWeb?: boolean }} [opts]
 */
export function buildSystemPromptTemplate(opts = {}) {
  const P = PROMPT_PLACEHOLDERS;
  const allowWeb = opts.allowWeb === true;
  return `You are the ${P.STRATEGY_ROLE} for this Stock Forge Agentic ${P.PORTFOLIO_KIND}.
Strategy in use: ${P.STRATEGY_LABEL}.
${P.STRATEGY_IMPLIES}

${OUTPUT_SCHEMA_CORE}
${buildWebResearchRules(allowWeb)}`;
}

/**
 * JSON payload skeleton with placeholders (matches adviseOpensWithCursor payload).
 */
export function buildPayloadTemplateSkeleton() {
  const P = PROMPT_PLACEHOLDERS;
  return `{
  "role": "${P.STRATEGY_ROLE}",
  "strategy": "${P.STRATEGY}",
  "strategyImplies": ${P.STRATEGY_IMPLIES_JSON},
  "slot": "${P.SLOT}",
  "want": ${P.WANT},
  "settings": ${P.AGENT_SETTINGS},
  "book": {
    "cash": ${P.BOOK_CASH},
    "buyingPower": ${P.BOOK_BUYING_POWER},
    "equity": ${P.BOOK_EQUITY},
    "reservedCollateral": ${P.BOOK_RESERVED},
    "margin": {
      "cashSecured": true,
      "buyingPower": ${P.BOOK_BUYING_POWER},
      "reservedCollateral": ${P.BOOK_RESERVED}
    },
    "openOptionPositions": ${P.OPEN_POSITIONS},
    "equityPositions": ${P.EQUITY_POSITIONS},
    "owedShares": ${P.OWED_SHARES},
    "owedSharesSemantics": ${P.OWED_SHARES_SEMANTICS_JSON},
    "wheelEligibility": ${P.WHEEL_ELIGIBILITY},
    "recentFills": ${P.RECENT_FILLS}
  },
  "memory": ${P.RECENT_MEMORY},
  "feedback": ${P.FEEDBACK},
  "userDecisions": ${P.USER_DECISIONS},
  "candidates": ${P.CANDIDATES},
  "portfolioTrades": ${P.PORTFOLIO_TRADES},
  "userInstructions": ${P.USER_INSTRUCTIONS}
}`;
}

/**
 * Assemble the user message sent to Cursor (system text embedded + payload).
 * Must stay aligned with getCanonicalPromptTemplate.
 * @param {{
 *   systemPrompt: string,
 *   role: string,
 *   want: number,
 *   payload: Record<string, unknown>,
 *   allowWeb?: boolean,
 * }} opts
 */
export function buildAdviseUserPrompt(opts) {
  const want = Math.max(0, Math.round(Number(opts.want) || 0));
  const role = String(opts.role || "trader");
  const allowWeb = opts.allowWeb === true;
  return [
    opts.systemPrompt,
    "",
    `Timeslot decision. You are the ${role}. Propose up to ${want} open action(s) plus any close/hold/skip notes, or pass.`,
    buildFinalReplyRule(allowWeb),
    "",
    JSON.stringify(opts.payload, null, 2),
  ].join("\n");
}

/**
 * Full canonical template (system + user structure) with placeholders.
 * Shown in Agentic → Instructions → Cloud prompt template.
 * Reflects the agent's current Allow Web setting and documents the alternate.
 * @param {string} [strategyHint] unused for structure; kept for API clarity
 * @param {{ allowWeb?: boolean }} [opts]
 */
export function getCanonicalPromptTemplate(_strategyHint, opts = {}) {
  const P = PROMPT_PLACEHOLDERS;
  const allowWeb = opts.allowWeb === true;
  const system = buildSystemPromptTemplate({ allowWeb });
  const finalRule = buildFinalReplyRule(allowWeb);
  const userBody = [
    P.SYSTEM_PROMPT,
    "",
    `Timeslot decision. You are the ${P.STRATEGY_ROLE}. Propose up to ${P.WANT} open action(s) plus any close/hold/skip notes, or pass.`,
    finalRule,
    "",
    buildPayloadTemplateSkeleton(),
  ].join("\n");

  const altRules = buildWebResearchRules(!allowWeb);
  const altFinal = buildFinalReplyRule(!allowWeb);

  return [
    "════════════════════════════════════════════════════════════",
    "CLOUD PROMPT TEMPLATE — Cursor paper-agent advise",
    "Placeholders like <OPEN_POSITIONS> are filled with live paper/SF+ data at advise time.",
    "Your editable Instructions appear inside <USER_INSTRUCTIONS> / settings.instructions.",
    `Allow Web (settings.allowWeb): ${allowWeb ? "ON" : "OFF"} — template below matches the current setting.`,
    "════════════════════════════════════════════════════════════",
    "",
    "── SYSTEM ──",
    system,
    "",
    "── USER (what Cursor agent.send receives) ──",
    userBody,
    "",
    "── ALLOW WEB TOGGLE (what changes) ──",
    `Current: allowWeb=${allowWeb}.`,
    "When toggled the opposite way, only these lines swap (JSON schema stays the same):",
    "",
    `Alternate web-research rules (allowWeb=${!allowWeb}):`,
    altRules,
    "",
    `Alternate final-reply line: ${altFinal}`,
    "",
    "Runtime note: Cloud agents already have VM network + computer-use/browser.",
    "allowWeb=false forbids tool use via prompt (JSON-only). allowWeb=true relaxes that",
    "so browse/search may run before the final JSON. No extra MCP is required for basic web;",
    "team MCP servers from cursor.com/agents may still be available if configured.",
    "",
    "── PLACEHOLDER KEY ──",
    `${P.STRATEGY} — strategy id (wheel | discretion)`,
    `${P.STRATEGY_LABEL} — display label (Wheel | Discretion)`,
    `${P.STRATEGY_ROLE} — e.g. Wheel strategy trader / live-portfolio Wheel advisor`,
    `${P.STRATEGY_IMPLIES} — bullet list of strategy rules`,
    `${P.STRATEGY_IMPLIES_JSON} — same rules as JSON array`,
    `${P.AGENT_KIND} — paper | advisor`,
    `${P.PORTFOLIO_KIND} — paper portfolio vs live brokerage portfolio (recommendations only)`,
    `${P.BOOK_SOURCE} — paper_ledger | live_portfolio`,
    `${P.SLOT} — timeslot id (e.g. 10:30 or advise-test)`,
    `${P.WANT} — max new opens this slot`,
    `${P.AGENT_SETTINGS} — full agent knobs (slots, risk, stop loss, model, capital, rank-by, keep-agent, allowWeb, …)`,
    `${P.ACCOUNT_VALUE} — income-targeting basis: own capital (paper) or live account value (advisor)`,
    `${P.TARGET_INCOME} — monthly $ goal ≈ expectedMonthlyRocPct × account value`,
    `${P.USER_INSTRUCTIONS} — this agent’s free-text Instructions (editable above)`,
    `${P.ALLOW_WEB} — boolean settings.allowWeb (also mirrored in packed settings JSON)`,
    `${P.WEB_RESEARCH_RULES} — allowWeb on/off tool-use rules (see SYSTEM + ALLOW WEB TOGGLE)`,
    `${P.FINAL_REPLY_RULE} — immediate JSON vs research-then-JSON user line`,
    `${P.CASH_AND_BUYING_POWER} — legacy alias; prefer ${P.BOOK_CASH} / ${P.BOOK_BUYING_POWER} / ${P.BOOK_EQUITY} / ${P.BOOK_RESERVED}`,
    `${P.BOOK_CASH} / ${P.BOOK_BUYING_POWER} / ${P.BOOK_EQUITY} / ${P.BOOK_RESERVED} — book cash figures`,
    `${P.OPEN_POSITIONS} — open short option positions (symbol, qty, strikes, expiry, side, entryPremium, mid, …)`,
    `${P.EQUITY_POSITIONS} — long stock / share lots from holdings book (summary)`,
    `${P.OWED_SHARES} — full owed-share context for covered calls (held, reserved, free, avg cost, related short calls). Semantics: long SF+ holdings to call away — not short-stock debt; no assignment-lot tags in ledger`,
    `${P.OWED_SHARES_SEMANTICS_JSON} — owed-share semantics prose (JSON string)`,
    `${P.WHEEL_ELIGIBILITY} — free-share / CSP-only notes when strategy is Wheel (includes owedShareTickers)`,
    `${P.RECENT_FILLS} / ${P.PORTFOLIO_TRADES} — recent paper fills / trade ledger context`,
    `${P.RECENT_MEMORY} — prior Cursor advise decisions (Linode-injected memory)`,
    `${P.FEEDBACK} — prior Cursor feedback (questions / promptIdeas / notes) from recent advise memory; "None yet" when empty`,
    `${P.USER_DECISIONS} — the trader's accepted / dismissed verdicts on prior recommendations plus their comments; "None yet" when empty`,
    `${P.CANDIDATES} — SF+ recommendation / scored candidate universe for opens`,
    `${P.SYSTEM_PROMPT} — copy of the SYSTEM block (embedded at top of user message)`,
  ].join("\n");
}

/**
 * Previous short bootstrap (pre Wheel playbook). Soft-migrate stores still on this text.
 * Uses OUTPUT_SCHEMA_CORE_PRE_SUMMARY so equality matches tenant stores before schema refresh.
 */
export function getLegacyDefaultBootstrapPromptBody() {
  const P = PROMPT_PLACEHOLDERS;
  return [
    `You are the ${P.STRATEGY_ROLE} for this Stock Forge Agentic paper portfolio.`,
    `Strategy in use: ${P.STRATEGY_LABEL}.`,
    P.STRATEGY_IMPLIES,
    "",
    "These are standing instructions for this paper-trading session. Remember them for later timeslot messages.",
    "",
    OUTPUT_SCHEMA_CORE_PRE_SUMMARY,
    P.WEB_RESEARCH_RULES,
    "",
    "BOOTSTRAP ONLY — do not propose trades yet. Live book, candidates, and want arrive in the next message.",
    'Acknowledge with ONLY valid JSON: {"ok":true}',
  ].join("\n");
}

/**
 * Previous enriched Wheel bootstrap (before hard cash/collateral standing rule).
 * Soft-migrate untouched wheel-default stores still on this text.
 */
export function getLegacyEnrichedBootstrapPromptBodyPreCollateral() {
  const P = PROMPT_PLACEHOLDERS;
  return [
    `You are the ${P.STRATEGY_ROLE} for this Stock Forge Agentic paper portfolio.`,
    `Strategy in use: ${P.STRATEGY_LABEL}.`,
    P.STRATEGY_IMPLIES,
    "",
    "These are standing instructions for this paper-trading session. Remember them for later timeslot messages.",
    "",
    "OBJECTIVE",
    `Generate risk-controlled Wheel premium income toward at least ${P.TARGET_INCOME}/month (from expectedMonthlyRocPct × account value ${P.ACCOUNT_VALUE}; see also ${P.AGENT_SETTINGS}).`,
    "Cycle: (1) sell cash-secured puts on liquid, fundamentally acceptable names, (2) accept assignment when consistent with the strategy, (3) sell covered calls against owed/held shares, (4) repeat while protecting capital.",
    "Premiums received count toward the monthly income goal (adjust if a short option is closed before expiry).",
    "Optimize risk-adjusted income and capital preservation — not trade frequency or the appearance of steady income. Pass / no-trade is often best.",
    "",
    "ACCOUNT & SETTINGS (live each slot)",
    `Hard paper/Wheel constraints are in ${P.STRATEGY_IMPLIES}. Live knobs arrive in ${P.AGENT_SETTINGS} and book figures (${P.BOOK_CASH}, ${P.BOOK_EQUITY}, ${P.BOOK_BUYING_POWER}, ${P.BOOK_RESERVED}, ${P.OPEN_POSITIONS}, ${P.OWED_SHARES}, ${P.WHEEL_ELIGIBILITY}).`,
    "Use settings when present: initialCapital, expectedMonthlyRocPct, maxOpenPositions, maxContractsPerTrade, minPop, minAnnRoc, maxOpenDtePuts/Calls, tradableOnly, takeProfitPctOfCredit / takeProfitDiscretion, stopLossMode, allowRiskyCalls / riskyCallsMinPop, focusTickers, sources.",
    "Liquidity / delta / volume / OI / spread / earnings: use SF+ candidate metrics when present; do not invent missing fields. Prefer liquid chains and reject wide markets when tradableOnly or candidate data show poor liquidity.",
    "",
    "CORE RULES",
    "- Cash-secured puts and covered calls only. Never propose naked calls, uncovered puts, leverage, short shares, or undefined-risk spreads.",
    "- Only sell puts on stocks the account is willing and able to own in 100-share lots.",
    "- Secured capital ≈ strike × 100 × contracts. Include existing shares, short options, pending exposure, and correlated names when sizing.",
    "- Do not allocate capital merely because cash is available. Reject poor liquidity, excessive concentration, binary-event risk, or inadequate downside compensation.",
    "- Prefer limit-style pricing near the option midpoint (midpoint is an estimate, not a fill guarantee). Propose actions in JSON; Linode paper rules gate/execute fills and may reject or resize — no live broker orders and no human-approval letter.",
    "",
    "UNDERLYING SELECTION",
    "Evaluate candidates on: business quality, valuation/downside, trend and vol regime, IV (rank/percentile when available), option-chain liquidity, earnings/dividends/catalysts, sector correlation, and whether owning 100 shares at the effective cost basis remains attractive after a substantial decline.",
    "Avoid: high-premium-only names, illiquid/wide chains, insolvency or severe dilution risk, leveraged/inverse ETFs unless instructions authorize them, and earnings exposure when catalysts look unfavorable and data supports skipping.",
    "",
    "CASH-SECURED PUT PROCESS",
    "For each proposed put: confirm cash/buying power for full assignment; pick a share-own price that is reasonable; choose expiration/strike consistent with maxOpenDtePuts and candidate PoP/delta when shown; estimate premium, max profit, break-even / effective purchase price, secured capital, return on secured capital, annualized return, distance to strike, and probability metrics labeled as estimates; note what would make the trade unsuitable; sketch a management plan (hold / BTC / assignment) before opening.",
    "",
    "COVERED-CALL PROCESS",
    "For each covered call: confirm owedShares / freeSharesForCalls allow the contracts; track strategy-adjusted cost basis (avgCost) separately from tax basis; prefer strikes at/above desired exit / avgCost unless allowRiskyCalls applies; explain upside opportunity cost; watch dividends and early-assignment risk; do not sell below the desired exit merely for more premium unless settings/instructions accept that outcome.",
    "",
    "POSITION MANAGEMENT",
    "Review opens using underlying price, option mid / extrinsic, delta/PoP estimates, DTE, earnings/ex-div when known, P&L, buying power, and concentration.",
    "Possible actions: hold, buy-to-close, close after capturing roughly takeProfitPctOfCredit (or discretionary ~50–75%) of max premium when risk/reward justifies, accept assignment, or allow expiration when appropriate. Account credits/debits across the cycle.",
    "",
    "ASSIGNMENT LOGIC",
    "Put assigned: record shares, strike, premium, effective strategy cost basis; reassess the thesis before mechanically selling a call — if thesis broken, consider closing shares instead of continuing the Wheel.",
    "Call assigned: compute cycle return (puts + shares + calls + dividends − fees when known); decide whether another put cycle on that name remains attractive.",
    "",
    "DATA INTEGRITY",
    "- Never invent quotes, Greeks, earnings dates, holdings, balances, or fills. Prefer packed book/candidates; if allowWeb, research only when a missing fact could change the decision.",
    "- Open positions include entryPremium (credit at open) and mid (current bid/ask midpoint from quotes — same as Puts/Calls screeners; use mid for BTC / take-profit / stop-loss, not broker mark).",
    "- If essential data is missing or stale, pass or ask via feedback.questions rather than guessing.",
    "- Separate facts, estimates, and judgment in rationale/summary.",
    "",
    "SLOT OUTPUT FORMAT (for later timeslot messages — not this bootstrap ack)",
    OUTPUT_SCHEMA_CORE,
    P.WEB_RESEARCH_RULES,
    "",
    "BOOTSTRAP ONLY — do not propose trades yet. Live book, candidates, want, and settings arrive in the next message.",
    'Acknowledge with ONLY valid JSON: {"ok":true}',
  ].join("\n");
}

/**
 * Standing bootstrap prompt (create path only). Wheel playbook + schema — no trade actions yet.
 * Reply ack only; trade JSON comes from the later slot turn.
 */
export function getDefaultBootstrapPromptBody() {
  const P = PROMPT_PLACEHOLDERS;
  return [
    `You are the ${P.STRATEGY_ROLE} for this Stock Forge Agentic ${P.PORTFOLIO_KIND}.`,
    `Strategy in use: ${P.STRATEGY_LABEL}. Agent kind: ${P.AGENT_KIND}. Book source: ${P.BOOK_SOURCE}.`,
    P.STRATEGY_IMPLIES,
    "",
    `These are standing instructions for this ${P.PORTFOLIO_KIND} session. Remember them for later timeslot messages.`,
    "",
    "OBJECTIVE",
    `Generate risk-controlled Wheel premium income toward at least ${P.TARGET_INCOME}/month (from expectedMonthlyRocPct × account value ${P.ACCOUNT_VALUE}; see also ${P.AGENT_SETTINGS}).`,
    "Cycle: (1) sell cash-secured puts on liquid, fundamentally acceptable names, (2) accept assignment when consistent with the strategy, (3) sell covered calls against owed/held shares on the packed book, (4) repeat while protecting capital.",
    "Premiums received count toward the monthly income goal (adjust if a short option is closed before expiry).",
    "Optimize risk-adjusted income and capital preservation — not trade frequency or the appearance of steady income. Pass / no-trade is often best.",
    "",
    "ACCOUNT & SETTINGS (live each slot)",
    `Hard Wheel/cash constraints are in ${P.STRATEGY_IMPLIES}. Live knobs arrive in ${P.AGENT_SETTINGS} and book figures (${P.BOOK_CASH}, ${P.BOOK_EQUITY}, ${P.BOOK_BUYING_POWER}, ${P.BOOK_RESERVED}, ${P.OPEN_POSITIONS}, ${P.OWED_SHARES}, ${P.WHEEL_ELIGIBILITY}).`,
    `Book figures come from ${P.BOOK_SOURCE} (see ${P.STRATEGY_IMPLIES}). SF+ recommendations supply the candidate universe for new ideas.`,
    "Use settings when present: initialCapital, expectedMonthlyRocPct, maxOpenPositions, maxContractsPerTrade, minPop, minAnnRoc, maxOpenDtePuts/Calls, tradableOnly, takeProfitPctOfCredit / takeProfitDiscretion, stopLossMode, allowRiskyCalls / riskyCallsMinPop, focusTickers, sources.",
    "Liquidity / delta / volume / OI / spread / earnings: use SF+ candidate metrics when present; do not invent missing fields. Prefer liquid chains and reject wide markets when tradableOnly or candidate data show poor liquidity.",
    "",
    "CORE RULES",
    "- Cash-secured puts and covered calls only. Never propose naked calls, uncovered puts, leverage, short shares, or undefined-risk spreads.",
    "- Only sell puts on stocks the account is willing and able to own in 100-share lots.",
    "- HARD STANDING RULE: never propose open puts whose secured capital (strike × 100 × qty) exceeds available cash/buyingPower on the packed book. If nothing affordable fits, skip or pass — do not suggest mega CSPs the book cannot collateralize.",
    "- Covered calls only when book.owedShares shows freeSharesForCalls / canSellCoveredCall for that ticker on the packed book.",
    "- Secured capital ≈ strike × 100 × contracts. Include existing shares, short options, pending exposure, and correlated names when sizing.",
    "- Do not allocate capital merely because cash is available. Reject poor liquidity, excessive concentration, binary-event risk, or inadequate downside compensation.",
    "- Prefer limit-style pricing near the option midpoint (midpoint is an estimate, not a fill guarantee). Propose actions in JSON; Linode records or gates them per agent kind — no live broker orders and no human-approval letter.",
    "",
    "UNDERLYING SELECTION",
    "Evaluate candidates on: business quality, valuation/downside, trend and vol regime, IV (rank/percentile when available), option-chain liquidity, earnings/dividends/catalysts, sector correlation, and whether owning 100 shares at the effective cost basis remains attractive after a substantial decline.",
    "Avoid: high-premium-only names, illiquid/wide chains, insolvency or severe dilution risk, leveraged/inverse ETFs unless instructions authorize them, and earnings exposure when catalysts look unfavorable and data supports skipping.",
    "",
    "CASH-SECURED PUT PROCESS",
    "For each proposed put: first verify strike × 100 × qty ≤ available cash/buyingPower; if not, do not propose that open. Then pick a share-own price that is reasonable; choose expiration/strike consistent with maxOpenDtePuts and candidate PoP/delta when shown; estimate premium, max profit, break-even / effective purchase price, secured capital, return on secured capital, annualized return, distance to strike, and probability metrics labeled as estimates; note what would make the trade unsuitable; sketch a management plan (hold / BTC / assignment) before opening.",
    "",
    "COVERED-CALL PROCESS",
    "For each covered call: confirm packed-book owedShares / freeSharesForCalls allow the contracts; track strategy-adjusted cost basis (avgCost) separately from tax basis; prefer strikes at/above desired exit / avgCost unless allowRiskyCalls applies; explain upside opportunity cost; watch dividends and early-assignment risk; do not sell below the desired exit merely for more premium unless settings/instructions accept that outcome.",
    "",
    "POSITION MANAGEMENT",
    "Review opens using underlying price, option mid / extrinsic, delta/PoP estimates, DTE, earnings/ex-div when known, P&L, buying power, and concentration.",
    "Possible actions: hold, buy-to-close, close after capturing roughly takeProfitPctOfCredit (or discretionary ~50–75%) of max premium when risk/reward justifies, accept assignment, or allow expiration when appropriate. Account credits/debits across the cycle.",
    "Stop-loss vs Wheel: stopLossMode=stop → BTC at stopLossPctOfCredit. stopLossMode=keep → hold through (assignment OK). stopLossMode=either on CSPs → use Wheel judgment: mechanical BTC is NOT required when assignment remains attractive near effective cost basis with no thesis break; prefer hold/assignment over forced BTC on marginal ITM puts.",
    "",
    "ASSIGNMENT LOGIC",
    "Put assigned: record shares on the packed book, strike, premium, effective strategy cost basis; reassess the thesis before mechanically selling a call — if thesis broken, consider closing shares instead of continuing the Wheel.",
    "Call assigned: compute cycle return (puts + shares + calls + dividends − fees when known); decide whether another put cycle on that name remains attractive.",
    "Assignment is a core Wheel outcome — not a stop-loss failure when the thesis still holds.",
    "",
    "DATA INTEGRITY",
    "- Never invent quotes, Greeks, earnings dates, holdings, balances, or fills. Prefer packed book/candidates; if allowWeb, research only when a missing fact could change the decision.",
    "- Open positions include entryPremium (credit at open) and mid (current bid/ask midpoint from quotes — same as Puts/Calls screeners; use mid for BTC / take-profit / stop-loss, not broker mark).",
    `- book.openOptionPositions, equityPositions, and owedShares are ${P.BOOK_SOURCE} — follow ${P.STRATEGY_IMPLIES} for whether that is paper or live.`,
    "- Prefer book/screener mids over ad-hoc web quotes; discard non-USD or stale web prices.",
    "- If essential data is missing or stale, pass or ask via feedback.questions rather than guessing. Do not repeat unanswered questions already in <FEEDBACK>.",
    "- Separate facts, estimates, and judgment in rationale/summary.",
    "",
    "SLOT OUTPUT FORMAT (for later timeslot messages — not this bootstrap ack)",
    OUTPUT_SCHEMA_CORE,
    P.WEB_RESEARCH_RULES,
    "",
    "BOOTSTRAP ONLY — do not propose trades yet. Live book, candidates, want, and settings arrive in the next message.",
    'Acknowledge with ONLY valid JSON: {"ok":true}',
  ].join("\n");
}

/**
 * Previous short slot body — soft-migrate stores still on this text.
 */
export function getLegacyDefaultSlotPromptTemplateBody() {
  const P = PROMPT_PLACEHOLDERS;
  return [
    `Timeslot decision. You are the ${P.STRATEGY_ROLE}. Propose up to ${P.WANT} open action(s) plus any close/hold/skip notes, or pass.`,
    "Standing role, strategy rules, and JSON schema were set in the bootstrap turn (or earlier in this conversation). Follow them; do not restate the essay.",
    P.FINAL_REPLY_RULE,
    `Allow Web (settings.allowWeb): ${P.ALLOW_WEB}`,
    "",
    buildPayloadTemplateSkeleton(),
  ].join("\n");
}

/**
 * Short slot prompt (every advise). Live book/candidates; assumes standing rules already bootstrapped
 * (or still present in conversation history when keep-agent resume is on).
 */
export function getDefaultSlotPromptTemplateBody() {
  const P = PROMPT_PLACEHOLDERS;
  return [
    `Timeslot decision. You are the ${P.STRATEGY_ROLE}. Propose up to ${P.WANT} open action(s) plus any close/hold/skip notes, or pass.`,
    "Standing Wheel playbook and JSON schema were set in the bootstrap turn (or earlier in this conversation). Follow them; do not restate the essay.",
    "Reply with ONLY the slot JSON: summary, pass, actions[], optional feedback — no prose approval letters.",
    P.FINAL_REPLY_RULE,
    `Allow Web (settings.allowWeb): ${P.ALLOW_WEB}`,
    "",
    buildPayloadTemplateSkeleton(),
  ].join("\n");
}

/**
 * @deprecated Prefer getDefaultSlotPromptTemplateBody / getDefaultBootstrapPromptBody.
 * Kept as the editable slot body seed for the Instructions UI.
 */
export function getDefaultPromptTemplateBody() {
  return getDefaultSlotPromptTemplateBody();
}

/**
 * Pre-bootstrap combined default (system + slot in one message). Used only to detect
 * stored templates that still need migration into bootstrapBody + slot body.
 * Uses OUTPUT_SCHEMA_CORE_PRE_SUMMARY so equality still matches pre-playbook stores.
 */
export function getLegacyCombinedPromptTemplateBody() {
  const P = PROMPT_PLACEHOLDERS;
  return [
    `You are the ${P.STRATEGY_ROLE} for this Stock Forge Agentic paper portfolio.`,
    `Strategy in use: ${P.STRATEGY_LABEL}.`,
    P.STRATEGY_IMPLIES,
    "",
    OUTPUT_SCHEMA_CORE_PRE_SUMMARY,
    P.WEB_RESEARCH_RULES,
    "",
    `Timeslot decision. You are the ${P.STRATEGY_ROLE}. Propose up to ${P.WANT} open action(s) plus any close/hold/skip notes, or pass.`,
    P.FINAL_REPLY_RULE,
    "",
    buildPayloadTemplateSkeleton(),
  ].join("\n");
}

/**
 * Human-readable placeholder legend for the Instructions UI.
 */
export function getPlaceholderLegend() {
  const P = PROMPT_PLACEHOLDERS;
  return [
    "Bootstrap prompt — sent once on Agent.create (standing Wheel playbook + schema). Prefer strategy / settings / income placeholders; live book tokens are optional.",
    "Slot template — sent every advise (create after bootstrap, or resume). Include live book/candidates placeholders; reply JSON only (summary, pass, actions, optional feedback).",
    "",
    `${P.STRATEGY} — strategy id (wheel | discretion)`,
    `${P.STRATEGY_LABEL} — display label (Wheel | Discretion)`,
    `${P.STRATEGY_ROLE} — e.g. Wheel strategy trader`,
    `${P.STRATEGY_IMPLIES} — short hard Wheel/paper constraints (CSP/CC eligibility, rules gate)`,
    `${P.STRATEGY_IMPLIES_JSON} — same rules as JSON array`,
    `${P.SLOT} — timeslot id`,
    `${P.WANT} — max new opens this slot`,
    `${P.AGENT_SETTINGS} — full agent knobs JSON`,
    `${P.ACCOUNT_VALUE} — own capital (paper) or live account value (advisor); falls back to initialCapital`,
    `${P.TARGET_INCOME} — monthly $ goal ≈ expectedMonthlyRocPct × account value`,
    `${P.USER_INSTRUCTIONS} — this agent’s free-text Instructions (below)`,
    `${P.ALLOW_WEB} — boolean settings.allowWeb`,
    `${P.WEB_RESEARCH_RULES} — allowWeb on/off tool-use rules (bootstrap)`,
    `${P.FINAL_REPLY_RULE} — immediate JSON vs research-then-JSON (slot)`,
    `${P.BOOK_CASH} / ${P.BOOK_BUYING_POWER} / ${P.BOOK_EQUITY} / ${P.BOOK_RESERVED} — book cash figures (slot)`,
    `${P.OPEN_POSITIONS} — open short option positions on the private paper book (slot)`,
    `${P.EQUITY_POSITIONS} — paper long stock / share lots (slot; not live SF+)`,
    `${P.OWED_SHARES} — paper owed-share context for covered calls (slot)`,
    `${P.OWED_SHARES_SEMANTICS_JSON} — owed-share semantics prose (JSON string)`,
    `${P.WHEEL_ELIGIBILITY} — Wheel free-share / CSP-only notes + unaffordable-put filter (slot)`,
    `${P.RECENT_FILLS} / ${P.PORTFOLIO_TRADES} — recent fills / trades (slot)`,
    `${P.RECENT_MEMORY} — prior Cursor advise memory (slot)`,
    `${P.FEEDBACK} — prior Cursor feedback loop (questions / promptIdeas / notes); "None yet" when empty (slot)`,
    `${P.USER_DECISIONS} — trader accept / dismiss verdicts + comments on prior recommendations (slot)`,
    `${P.CANDIDATES} — SF+ candidate universe (slot)`,
    `${P.SYSTEM_PROMPT} — optional filled system block`,
  ].join("\n");
}

/**
 * Soft-migrate saved template bodies: inject <FEEDBACK>, refresh schema text, and
 * replace the short default bootstrap with the enriched Wheel playbook when unchanged.
 * @param {string} body
 * @param {string} bootstrapBody
 * @returns {{ body: string, bootstrapBody: string }}
 */
export function migrateTemplateBodiesForFeedback(body, bootstrapBody) {
  let nextBody = String(body || "");
  let nextBootstrap = String(bootstrapBody || "");
  if (nextBody && !nextBody.includes("<FEEDBACK>")) {
    if (nextBody.includes('"memory": <RECENT_MEMORY>,')) {
      nextBody = nextBody.replace(
        '"memory": <RECENT_MEMORY>,',
        '"memory": <RECENT_MEMORY>,\n  "feedback": <FEEDBACK>,',
      );
    } else if (nextBody.includes('"memory": <RECENT_MEMORY>')) {
      nextBody = nextBody.replace(
        '"memory": <RECENT_MEMORY>',
        '"memory": <RECENT_MEMORY>,\n  "feedback": <FEEDBACK>',
      );
    }
  }
  if (nextBody && !nextBody.includes("<USER_DECISIONS>")) {
    if (nextBody.includes('"feedback": <FEEDBACK>,')) {
      nextBody = nextBody.replace(
        '"feedback": <FEEDBACK>,',
        '"feedback": <FEEDBACK>,\n  "userDecisions": <USER_DECISIONS>,',
      );
    } else if (nextBody.includes('"feedback": <FEEDBACK>')) {
      nextBody = nextBody.replace(
        '"feedback": <FEEDBACK>',
        '"feedback": <FEEDBACK>,\n  "userDecisions": <USER_DECISIONS>',
      );
    }
  }
  if (nextBody && nextBody.includes(OUTPUT_SCHEMA_CORE_LEGACY)) {
    nextBody = nextBody.split(OUTPUT_SCHEMA_CORE_LEGACY).join(OUTPUT_SCHEMA_CORE);
  }
  if (nextBody && nextBody.includes(OUTPUT_SCHEMA_CORE_PRE_SUMMARY)) {
    nextBody = nextBody.split(OUTPUT_SCHEMA_CORE_PRE_SUMMARY).join(OUTPUT_SCHEMA_CORE);
  }
  if (nextBootstrap && nextBootstrap.includes(OUTPUT_SCHEMA_CORE_LEGACY)) {
    nextBootstrap = nextBootstrap.split(OUTPUT_SCHEMA_CORE_LEGACY).join(OUTPUT_SCHEMA_CORE);
  }
  if (nextBootstrap && nextBootstrap.includes(OUTPUT_SCHEMA_CORE_PRE_SUMMARY)) {
    nextBootstrap = nextBootstrap
      .split(OUTPUT_SCHEMA_CORE_PRE_SUMMARY)
      .join(OUTPUT_SCHEMA_CORE);
  }
  for (const key of ["body", "bootstrapBody"]) {
    const src = key === "body" ? nextBody : nextBootstrap;
    if (!src) continue;
    const migrated = src.replace(/option mark \/ extrinsic/g, "option mid / extrinsic");
    if (key === "body") nextBody = migrated;
    else nextBootstrap = migrated;
  }
  // Refresh built-in / untouched short or pre-collateral bootstrap → current Wheel playbook.
  const legacyShort = getLegacyDefaultBootstrapPromptBody();
  const legacyShortWithLegacySchema = legacyShort
    .split(OUTPUT_SCHEMA_CORE_PRE_SUMMARY)
    .join(OUTPUT_SCHEMA_CORE_LEGACY);
  const legacyShortWithNewSchema = legacyShort
    .split(OUTPUT_SCHEMA_CORE_PRE_SUMMARY)
    .join(OUTPUT_SCHEMA_CORE);
  const legacyEnrichedPreCollateral = getLegacyEnrichedBootstrapPromptBodyPreCollateral();
  if (
    !nextBootstrap ||
    nextBootstrap === legacyShort ||
    nextBootstrap === legacyShortWithLegacySchema ||
    nextBootstrap === legacyShortWithNewSchema ||
    nextBootstrap === legacyEnrichedPreCollateral
  ) {
    nextBootstrap = getDefaultBootstrapPromptBody();
  }
  const legacySlot = getLegacyDefaultSlotPromptTemplateBody();
  if (!nextBody || nextBody === legacySlot) {
    nextBody = getDefaultSlotPromptTemplateBody();
  }
  return { body: nextBody, bootstrapBody: nextBootstrap };
}

/**
 * Normalize model feedback (string or object) into a stable shape.
 * @param {unknown} raw
 * @returns {{ questions: string[], promptIdeas: string[], notes: string } | null}
 */
export function normalizeFeedback(raw) {
  if (raw == null || raw === false) return null;
  if (typeof raw === "string") {
    const notes = raw.trim().slice(0, 2000);
    return notes ? { questions: [], promptIdeas: [], notes } : null;
  }
  if (typeof raw !== "object" || Array.isArray(raw)) return null;
  const obj = /** @type {Record<string, unknown>} */ (raw);
  const asList = (value) => {
    if (typeof value === "string" && value.trim()) {
      return [value.trim().slice(0, 500)];
    }
    if (!Array.isArray(value)) return [];
    return value
      .map((x) => String(x ?? "").trim())
      .filter(Boolean)
      .map((s) => s.slice(0, 500))
      .slice(0, 12);
  };
  const questions = asList(obj.questions ?? obj.Questions);
  const promptIdeas = asList(obj.promptIdeas ?? obj.prompt_ideas ?? obj.ideas);
  const notesRaw =
    typeof obj.notes === "string"
      ? obj.notes
      : typeof obj.note === "string"
        ? obj.note
        : "";
  const notes = notesRaw.trim().slice(0, 2000);
  if (!questions.length && !promptIdeas.length && !notes) return null;
  return { questions, promptIdeas, notes };
}

/**
 * Compact the human accept/dismiss verdicts on prior recommendations.
 * Tolerates raw recommendations (userStatus/userNote) and already-compacted rows
 * (decision/comment) so repeated passes stay stable.
 * @param {unknown} decisions
 * @returns {Array<Record<string, unknown>>}
 */
export function compactUserDecisions(decisions) {
  if (!Array.isArray(decisions)) return [];
  /** @type {Array<Record<string, unknown>>} */
  const out = [];
  for (const raw of decisions) {
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) continue;
    const rec = /** @type {Record<string, unknown>} */ (raw);
    const status = String(rec.userStatus ?? rec.decision ?? "")
      .trim()
      .toLowerCase();
    if (status !== "accepted" && status !== "dismissed") continue;
    const comment = String(rec.userNote ?? rec.comment ?? "").trim();
    const num = (value) => {
      const n = Number(value);
      return Number.isFinite(n) ? n : null;
    };
    out.push({
      decision: status,
      decidedAt: String(rec.userStatusAt ?? rec.decidedAt ?? "") || null,
      advisedAt: String(rec.ts ?? rec.advisedAt ?? "") || null,
      action: String(rec.action ?? "") || null,
      underlying: String(rec.underlying ?? "") || null,
      side: String(rec.side ?? "") || null,
      strike: num(rec.strike),
      expirationDate: String(rec.expirationDate ?? "") || null,
      quantity: num(rec.quantity),
      premium: num(rec.premium),
      comment: comment ? comment.slice(0, 500) : null,
    });
  }
  return out;
}

/**
 * Compact human verdicts for the <USER_DECISIONS> placeholder.
 * @param {unknown} decisions
 * @returns {string} JSON text suitable for template substitution
 */
export function formatUserDecisionsForPrompt(decisions) {
  const items = compactUserDecisions(decisions);
  return JSON.stringify(items.length ? items : "None yet", null, 2);
}

/**
 * Compact prior feedback for the <FEEDBACK> placeholder.
 * @param {unknown} memoryOrFeedback
 * @returns {string} JSON text suitable for template substitution
 */
export function formatFeedbackForPrompt(memoryOrFeedback) {
  const json = (value) => JSON.stringify(value, null, 2);
  const normQ = (q) =>
    String(q || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ")
      .slice(0, 240);
  if (Array.isArray(memoryOrFeedback)) {
    /** @type {Array<Record<string, unknown>>} */
    const items = [];
    /** @type {Set<string>} */
    const seenQuestions = new Set();
    for (const turn of memoryOrFeedback) {
      if (!turn || typeof turn !== "object") continue;
      const row = /** @type {Record<string, unknown>} */ (turn);
      const fb = normalizeFeedback(row.feedback);
      if (!fb) continue;
      const questions = [];
      for (const q of fb.questions || []) {
        const key = normQ(q);
        if (!key || seenQuestions.has(key)) continue;
        // Treat near-duplicates that only re-flag "still unresolved" as the same question.
        let dup = false;
        for (const prev of seenQuestions) {
          if (key.includes(prev.slice(0, 80)) || prev.includes(key.slice(0, 80))) {
            dup = true;
            break;
          }
        }
        if (dup) continue;
        seenQuestions.add(key);
        questions.push(q);
      }
      const deduped = {
        questions,
        promptIdeas: fb.promptIdeas || [],
        notes: fb.notes || "",
      };
      if (!deduped.questions.length && !deduped.promptIdeas.length && !deduped.notes) {
        continue;
      }
      items.push({
        ts: row.ts || null,
        slot: row.slot || null,
        feedback: deduped,
      });
    }
    if (!items.length) return json("None yet");
    return json(items);
  }
  const single = normalizeFeedback(memoryOrFeedback);
  if (!single) return json("None yet");
  return json(single);
}

/**
 * Substitute placeholders in a saved template body.
 * Longer tokens are replaced first to avoid partial overlaps.
 * @param {string} body
 * @param {Record<string, string | number | boolean | null | undefined>} values
 *   Keys may be bare names (OPEN_POSITIONS) or full tokens (<OPEN_POSITIONS>).
 */
export function applyPromptTemplate(body, values) {
  let out = String(body || "");
  if (!out) return out;
  /** @type {Array<[string, string]>} */
  const pairs = [];
  for (const [key, raw] of Object.entries(values || {})) {
    const token = key.startsWith("<") ? key : `<${key}>`;
    const text =
      raw == null ? "" : typeof raw === "string" ? raw : String(raw);
    pairs.push([token, text]);
  }
  pairs.sort((a, b) => b[0].length - a[0].length);
  for (const [token, text] of pairs) {
    if (!out.includes(token)) continue;
    out = out.split(token).join(text);
  }
  return out;
}

/**
 * Build the placeholder → string map used at advise time.
 * JSON-shaped placeholders are pre-stringified (no extra quotes in the template).
 * @param {{
 *   strategy?: string,
 *   allowWeb?: boolean,
 *   slot?: string | null,
 *   want?: number,
 *   settingsForPrompt?: Record<string, unknown>,
 *   userInstructions?: string,
 *   cash?: number,
 *   buyingPower?: number,
 *   equity?: number,
 *   reservedCollateral?: number,
 *   openPositions?: unknown,
 *   equityPositions?: unknown,
 *   owedShares?: unknown,
 *   wheelEligibility?: unknown,
 *   recentFills?: unknown,
 *   portfolioTrades?: unknown,
 *   memory?: unknown,
 *   feedback?: unknown,
 *   candidates?: unknown,
 *   systemPrompt?: string,
 * }} ctx
 */
export function buildPromptTemplateValues(ctx = {}) {
  const playbook = strategyPlaybook(ctx.strategy, { kind: ctx.kind });
  const label = playbook.strategy === "wheel" ? "Wheel" : "Discretion";
  const allowWeb = ctx.allowWeb === true;
  const kind = playbook.kind === "advisor" ? "advisor" : "paper";
  const portfolioKind =
    kind === "advisor"
      ? "live brokerage portfolio (recommendations only)"
      : "paper portfolio";
  const bookSource = kind === "advisor" ? "live_portfolio" : "paper_ledger";
  const want = Math.max(0, Math.round(Number(ctx.want) || 0));
  const instructions =
    typeof ctx.userInstructions === "string"
      ? ctx.userInstructions
      : String(ctx.settingsForPrompt?.instructions || "");
  const systemPrompt =
    typeof ctx.systemPrompt === "string" && ctx.systemPrompt
      ? ctx.systemPrompt
      : buildSystemPrompt(ctx.strategy, { allowWeb });
  const cash = Number(ctx.cash) || 0;
  const buyingPower = Number(ctx.buyingPower ?? ctx.cash) || 0;
  const equity = Number(ctx.equity ?? ctx.cash) || 0;
  const reserved = Number(ctx.reservedCollateral) || 0;
  const initialCapital = Number(ctx.settingsForPrompt?.initialCapital) || 0;
  const accountValue = equity > 0 ? equity : initialCapital;
  const monthlyRocPct = Number(ctx.settingsForPrompt?.expectedMonthlyRocPct) || 0;
  const targetIncome =
    accountValue > 0 && monthlyRocPct > 0
      ? Math.round(((accountValue * monthlyRocPct) / 100) * 100) / 100
      : 0;
  const fmtUsd = (n) =>
    `$${Number(n || 0).toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })}`;
  const json = (value) => JSON.stringify(value ?? null, null, 2);
  const feedbackText =
    ctx.feedback !== undefined
      ? formatFeedbackForPrompt(ctx.feedback)
      : formatFeedbackForPrompt(ctx.memory || []);
  const P = PROMPT_PLACEHOLDERS;
  return {
    [P.STRATEGY]: playbook.strategy,
    [P.STRATEGY_LABEL]: label,
    [P.STRATEGY_ROLE]: playbook.role,
    [P.STRATEGY_IMPLIES]: playbook.implies.map((line) => `- ${line}`).join("\n"),
    [P.STRATEGY_IMPLIES_JSON]: json(playbook.implies),
    [P.AGENT_KIND]: kind,
    [P.PORTFOLIO_KIND]: portfolioKind,
    [P.BOOK_SOURCE]: bookSource,
    [P.SLOT]: String(ctx.slot || ""),
    [P.WANT]: String(want),
    [P.AGENT_SETTINGS]: json(ctx.settingsForPrompt || {}),
    [P.ACCOUNT_VALUE]: fmtUsd(accountValue),
    [P.TARGET_INCOME]:
      targetIncome > 0
        ? fmtUsd(targetIncome)
        : "(set expectedMonthlyRocPct × capital in agent settings)",
    [P.USER_INSTRUCTIONS]: json(instructions),
    [P.CASH_AND_BUYING_POWER]: String(cash),
    [P.BOOK_CASH]: String(cash),
    [P.BOOK_BUYING_POWER]: String(buyingPower),
    [P.BOOK_EQUITY]: String(equity),
    [P.BOOK_RESERVED]: String(reserved),
    [P.OPEN_POSITIONS]: json(ctx.openPositions || []),
    [P.EQUITY_POSITIONS]: json(ctx.equityPositions || []),
    [P.OWED_SHARES]: json(ctx.owedShares || []),
    [P.OWED_SHARES_SEMANTICS_JSON]: json(
      ctx.owedSharesSemantics ||
        (kind === "advisor" ? LIVE_OWED_SHARES_SEMANTICS : OWED_SHARES_SEMANTICS),
    ),
    [P.WHEEL_ELIGIBILITY]: json(ctx.wheelEligibility ?? null),
    [P.RECENT_FILLS]: json(ctx.recentFills || []),
    [P.PORTFOLIO_TRADES]: json(ctx.portfolioTrades || ctx.recentFills || []),
    [P.RECENT_MEMORY]: json(ctx.memory || []),
    [P.FEEDBACK]: feedbackText,
    [P.USER_DECISIONS]: formatUserDecisionsForPrompt(ctx.userDecisions || []),
    [P.CANDIDATES]: json(ctx.candidates || []),
    [P.SYSTEM_PROMPT]: systemPrompt,
    [P.ALLOW_WEB]: allowWeb ? "true" : "false",
    [P.WEB_RESEARCH_RULES]: buildWebResearchRules(allowWeb),
    [P.FINAL_REPLY_RULE]: buildFinalReplyRule(allowWeb),
  };
}

/**
 * @param {unknown} value
 */
export function normalizePromptTemplateName(value) {
  const name = String(value || "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, MAX_TEMPLATE_NAME_CHARS);
  return name || "Untitled template";
}

/**
 * @param {unknown} value
 */
export function normalizePromptTemplateBody(value) {
  if (typeof value !== "string") return "";
  return value.replace(/\r\n/g, "\n").slice(0, MAX_TEMPLATE_BODY_CHARS);
}
